import { useEffect, useRef } from 'react';

/*
 * The message beside a field that failed validation (see utils/formValidation).
 *
 * Rendered only while there is a message. `id` is what the field points at
 * through aria-describedby (invalidProps), so the message is read as part of
 * the field whenever it takes focus -- which is where focusFirstInvalid()
 * sends it after a refused submit.
 *
 * Deliberately NOT a live region (no role="alert"): a refused submit puts
 * every message on screen at once AND moves focus to the first field, which
 * already reads its own message. As alerts they arrived as one assertive
 * burst, out of context, interrupting that very announcement; this way each
 * is read beside its field as the person reaches it.
 *
 * Colour is the danger token on purpose and the text says what to do, so the
 * error never rests on colour alone.
 */
export const FIELD_ERROR_STYLE = {
  display: 'block',
  color: 'var(--c-danger-fg)',
  fontSize: 13,
  fontWeight: 400,
  lineHeight: 1.4,
  marginTop: 2,
};

/** The id of a field's message, from the field's own id. */
export const errorIdFor = (fieldId) => `${fieldId}-error`;

export default function FieldError({ id, children, testId }) {
  if (!children) return null;
  return (
    <span id={id} data-testid={testId} style={FIELD_ERROR_STYLE}>
      {children}
    </span>
  );
}

/**
 * aria for the field itself: aria-invalid, and aria-describedby naming its
 * message (plus any hint it is already described by) -- only while invalid.
 */
export function invalidProps(errorId, invalid, hintId) {
  if (!invalid) return hintId ? { 'aria-describedby': hintId } : {};
  return {
    'aria-invalid': true,
    'aria-describedby': [hintId, errorId].filter(Boolean).join(' '),
  };
}

/**
 * An invalid field's box: a danger-coloured border. Written as the `border`
 * shorthand because every form input here sets its border that way, and React
 * warns when a longhand is mixed into a shorthand on the same element.
 *
 * The -fg danger token: a border is a UI boundary that needs 3:1 against the
 * surface, and the plain danger red is about 2.6:1 on the dark theme's
 * surface. index.css draws the same border on anything aria-invalid, which
 * is what marks the pickers InvalidWithin wraps.
 */
export const invalidBorder = (invalid) => (invalid ? { border: '1px solid var(--c-danger-fg)' } : null);

/**
 * A wrapper for a control rendered by a component that does not pass aria
 * attributes through (RecordAutocomplete, ReferenceSelect). It sets
 * aria-invalid and aria-describedby on the first input/select/textarea inside
 * itself while `invalid`, and takes them off again after; with `required` it
 * also sets aria-required, so a picker marked "*" is required to a screen
 * reader too. Extra props (a data-field for focusFirstInvalid, a data-testid)
 * go on the wrapper.
 */
export function InvalidWithin({ invalid, errorId, required = false, children, style, ...rest }) {
  const ref = useRef(null);
  // No dependency list: the wrapped component may replace its control on any
  // render, and a stale element would keep the old state.
  useEffect(() => {
    const control = ref.current?.querySelector('input, select, textarea');
    if (!control) return;
    if (required) control.setAttribute('aria-required', 'true');
    else if (control.getAttribute('aria-required') === 'true') control.removeAttribute('aria-required');
    if (invalid) {
      control.setAttribute('aria-invalid', 'true');
      control.setAttribute('aria-describedby', errorId);
    } else if (control.getAttribute('aria-describedby') === errorId) {
      control.removeAttribute('aria-invalid');
      control.removeAttribute('aria-describedby');
    }
  });
  return <div ref={ref} style={style} {...rest}>{children}</div>;
}
